import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { baseUrl } from "../../constants/api";
import { API_ENDPOINTS } from "../../constants/endpoints";
import { ChartData } from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, ChartDataLabels);

interface DashboardChartsProps {
    token: string | null;
    topics: any[];
    selectedTopic: string | null;
    selectedSubtopic: string | null;
    userId?: string;
}

const SUBTOPIC_PALETTE = [
    '#f26565', // red-ish
    '#2b9691', // teal
    '#f0b84f', // amber
    '#6366f1', // indigo
    '#fcaf41', // orange
    '#ec4899', // pink
    '#8b5cf6', // violet
    '#10b981', // emerald
    '#f97316', // deep-orange
    '#06b6d4', // cyan
];

export const getSubtopicColor = (index: number) =>
    SUBTOPIC_PALETTE[index % SUBTOPIC_PALETTE.length];

export const resolveSubtopicIndex = (topics: any[], selectedTopic: string | null, label: string): number => {
    if (!selectedTopic) return -1;
    const topicObj = topics.find(t => t.topic_name === selectedTopic);
    if (!topicObj) return -1;
    return topicObj.subtopics.indexOf(label);
};

const DashboardCharts: React.FC<DashboardChartsProps> = ({ token, topics, selectedTopic, selectedSubtopic, userId }) => {
    const [timeFilter, setTimeFilter] = useState('Months');
    const [dashboardLineData, setDashboardLineData] = useState<ChartData<'line'> | null>(null);
    const [dashboardBarData, setDashboardBarData] = useState<ChartData<'bar'> | null>(null);
    const [dashboardDoughnutData, setDashboardDoughnutData] = useState<ChartData<'doughnut'> | null>(null);

    const { isFetching: isLineLoading } = useQuery({
        queryKey: ['dashboardLineChart', selectedTopic, selectedSubtopic, timeFilter, userId],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (selectedTopic) params.append('topic_name', selectedTopic);
            // Handle differences in "All / null" implementations smoothly. 
            // In AdminDashboard it's null, in QuizList it's "All" when all subtopics are selected.
            if (selectedSubtopic && selectedSubtopic !== "All") params.append('subtopic_name', selectedSubtopic);
            params.append('time_filter', timeFilter);
            if (userId) params.append('user_id', userId);

            const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_DASHBOARD_GRAPHS}?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    const graphData = result.data;
                    const lineDatasets = graphData.line.datasets.map((ds: any, idx: number) => {
                        const subIdx = resolveSubtopicIndex(topics, selectedTopic, ds.label);
                        const colorIdx = subIdx >= 0 ? subIdx : idx;
                        const color = getSubtopicColor(colorIdx);
                        return {
                            label: ds.label,
                            data: ds.data,
                            borderColor: color,
                            backgroundColor: color,
                            borderDash: [5, 5]
                        };
                    });
                    setDashboardLineData({
                        labels: graphData.line.labels,
                        datasets: lineDatasets
                    });
                    return graphData;
                }
            }
            return null;
        },
        enabled: !!token,
    });

    const { isFetching: isBarDoughnutLoading } = useQuery({
        queryKey: ['dashboardBarDoughnut', selectedTopic, timeFilter, userId],
        queryFn: async () => {
            if (!selectedTopic) return null; // Avoid fetching without a topic
            const params = new URLSearchParams();
            params.append('topic_name', selectedTopic);
            params.append('time_filter', timeFilter);
            if (userId) params.append('user_id', userId);

            const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_DASHBOARD_GRAPHS}?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    const graphData = result.data;

                    setDashboardBarData({
                        labels: graphData.bar.labels,
                        datasets: [{
                            data: graphData.bar.data,
                            backgroundColor: graphData.bar.labels.map((label: string, i: number) => {
                                const subIdx = resolveSubtopicIndex(topics, selectedTopic, label);
                                return getSubtopicColor(subIdx >= 0 ? subIdx : i);
                            }),
                            borderWidth: 1,
                            borderRadius: 4
                        }]
                    });

                    setDashboardDoughnutData({
                        labels: graphData.doughnut.labels,
                        datasets: [{
                            data: graphData.doughnut.data,
                            backgroundColor: graphData.doughnut.labels.map((label: string, i: number) => {
                                const subIdx = resolveSubtopicIndex(topics, selectedTopic, label);
                                return getSubtopicColor(subIdx >= 0 ? subIdx : i);
                            }),
                            borderWidth: 0
                        }]
                    });

                    return graphData;
                }
            }
            return null;
        },
        enabled: !!token && !!selectedTopic,
    });

    // removed isLoading = false

    return (
        <div className="w-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-bold m-0 p-0">Quiz Appear {selectedTopic ? `(${selectedSubtopic && selectedSubtopic !== "All" ? selectedSubtopic : selectedTopic})` : ''}</div>
                <div className="d-flex bg-light rounded shadow-sm" style={{ border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {['Days', 'Weeks', 'Months'].map(f => (
                        <button
                            key={f}
                            onClick={() => setTimeFilter(f)}
                            style={{
                                padding: '4px 12px',
                                fontSize: '13px',
                                fontWeight: '500',
                                border: 'none',
                                backgroundColor: timeFilter === f ? '#6366f1' : 'transparent',
                                color: timeFilter === f ? 'white' : '#64748b',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer'
                            }}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ height: "300px", width: "100%", marginBottom: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: !dashboardLineData ? '1px dashed #cbd5e1' : 'none' }}>
                {isLineLoading ? (
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : dashboardLineData && dashboardLineData.datasets && dashboardLineData.datasets.length > 0 ? (
                    <Line
                        data={dashboardLineData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: true, position: 'top' },
                                datalabels: { display: false }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    title: { display: true, text: 'No. of users Appear →' },
                                },
                                x: {
                                    title: { display: true, text: `${timeFilter} →` },
                                }
                            }
                        }}
                    />
                ) : (
                    <div className="text-muted italic">No analytics data available for this selection.</div>
                )}
            </div>

            <div className="row mt-4 g-4">
                <div className="col-12 col-xl-6">
                    <div className="fw-bold mb-1">Average Score</div>
                    <div className="text-muted small mb-3">Average marks obtain by users on each subtopic</div>
                    <div style={{ height: '300px', width: '100%', backgroundColor: '#fafafa', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="rounded">
                        {isBarDoughnutLoading ? (
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        ) : dashboardBarData && dashboardBarData.labels && dashboardBarData.labels.length > 0 ? (
                            <Bar
                                data={dashboardBarData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                        y: { beginAtZero: true, max: 100, title: { display: true, text: 'Avg Score (%) →' } },
                                        x: { title: { display: true, text: 'Subtopics →' } }
                                    },
                                    plugins: {
                                        legend: { display: false },
                                        datalabels: { display: false }
                                    }
                                }}
                            />
                        ) : (
                            <div className="text-muted small">No data</div>
                        )}
                    </div>
                </div>
                <div className="col-12 col-xl-6">
                    <div className="fw-bold mb-1">Number of Quizzes Completed</div>
                    <div className="text-muted small mb-3">Total quizzes completed across subtopics</div>
                    <div style={{ height: '300px', width: '100%', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="rounded">
                        {isBarDoughnutLoading ? (
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        ) : dashboardDoughnutData && dashboardDoughnutData.labels && dashboardDoughnutData.labels.length > 0 ? (
                            <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                                <Doughnut
                                    data={dashboardDoughnutData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        layout: {
                                            padding: 60
                                        },
                                        plugins: {
                                            legend: { display: false },
                                            datalabels: {
                                                display: true,
                                                borderRadius: 4,
                                                color: '#000',
                                                padding: 6,
                                                font: {
                                                    size: 11
                                                },
                                                formatter: (value: any, context: any) => {
                                                    const label = context.chart.data.labels[context.dataIndex];
                                                    return ['', `${label} : ${value}`];
                                                },
                                                anchor: 'end',
                                                align: 'end',
                                                offset: 4
                                            }
                                        },
                                        cutout: '60%'
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="text-muted small">No data</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardCharts;
